import { Tooltip } from "antd";
import { PIPELINE_BUILD_STATUS_CODES } from "../enums/enums";
import { CheckCircle2, XCircle, Loader2, Clock, AlertCircle } from 'lucide-react';
import React from 'react';

function getBuildStatusAccent(status: string, statusCode?: string) {
    switch (status) {
        case "SUCCEEDED":
            // If there's a statusCode, it means there's a warning (e.g., deployment unavailable)
            if (statusCode) {
                return { 
                    color: "#f59e0b", 
                    icon: <AlertCircle size={10} />
                }; // Warning orange
            }
            return { 
                color: "#059669", 
                icon: <CheckCircle2 size={10} />
            }; // More vibrant emerald green for success
        case "FAILED":
            return { 
                color: "#ef4444", 
                icon: <XCircle size={10} />
            }; // Error red
        case "RUNNING":
            return { 
                color: "#3b82f6", 
                icon: <Loader2 size={10} style={{ animation: 'spin 1s linear infinite' }} />,
                animate: true 
            }; // Processing blue with animation
        case "QUEUED":
            return { 
                color: "#9ca3af", 
                icon: <Clock size={10} />,
                animate: true 
            }; // Lighter gray with animation
        case "ABORTED":
            return { 
                color: "#f59e0b", 
                icon: <AlertCircle size={10} />
            }; // Warning orange
        default:
            return { 
                color: "#9ca3af", 
                icon: <Clock size={10} />
            }; // Lighter gray
    }
}

function getBuildStatusText(status: string): string {
    switch (status) {
        case "SUCCEEDED":
            return "Succeeded";
        case "FAILED":
            return "Failed";
        case "RUNNING":
            return "Building";
        case "QUEUED":
            return "Queued";
        case "ABORTED":
            return "Aborted";
        default:
            return "Unknown status";
    }
}

export default function BuildStatusTag(props: { status: string, statusCode?: string; size?: "small" | "large" }) {
    const statusAccent = getBuildStatusAccent(props.status, props.statusCode);
    const statusText = getBuildStatusText(props.status);
    const fontSize = props.size === "large" ? "10px" : "9px";
    const padding = props.size === "large" ? "3px 8px" : "2px 6px";
    const tooltipTitle = props.statusCode ? PIPELINE_BUILD_STATUS_CODES[props.statusCode] : undefined;
    
    // Make success states more prominent with darker text color
    const isSuccess = props.status === "SUCCEEDED" && !props.statusCode;
    const textColor = isSuccess ? "#047857" : statusAccent.color; // Darker green for better contrast
    const iconSize = props.size === "large" ? "11px" : "10px";

    const badge = (
        <div style={{
            fontSize: fontSize,
            fontWeight: 700, // Bolder font weight for success states
            textTransform: "uppercase",
            letterSpacing: "0.04em", // Reduced letter spacing for smaller size
            color: textColor,
            flexShrink: 0,
            padding: padding,
            borderRadius: "3px",
            backgroundColor: `${statusAccent.color}10`, // More subtle background
            border: `1px solid ${statusAccent.color}20`,
            display: "inline-flex",
            alignItems: "center",
            gap: "4px"
        }}>
            <span style={{ 
                color: statusAccent.color,
                display: "inline-flex",
                alignItems: "center"
            }}>
                {React.cloneElement(statusAccent.icon, { style: { ...statusAccent.icon.props.style, fontSize: iconSize } })}
            </span>
            {statusText}
        </div>
    );

    if (tooltipTitle) {
        return (
            <Tooltip title={tooltipTitle}>
                {badge}
            </Tooltip>
        );
    }

    return badge;
};